import { Box, Typography } from "@mui/material";
import { Moment } from "moment";
import { MouseEvent } from "react";
import { Task } from "../../../api/Tasks";
import { weekdayNames } from "../repeatText";
import { CalendarDay } from "./CalendarDay";
import { dateFormat } from "../../../misc/dates";
import { calendarDays, CalendarView, collapsesDay, DragTask } from "./range";
import { CalendarCollapsedDay } from "./CalendarCollapsedDay";

// a week view has one tall row, a month view has 5-6 shorter ones. Each is a floor:
// the rows share any height the grid is given beyond it.
const rowHeight = { "week": 420, "full-week": 420, "month": 120 };

export function CalendarGrid(props: {
    start: Moment;
    end: Moment;
    view: CalendarView;
    month: number;
    tasks: Task[];
    selectedId?: number;

    // selectedIds is the tasks picked for a bulk change
    selectedIds?: number[];
    saving?: boolean;

    // showTeam names each task's team, for the "All" workspace
    showTeam?: boolean;
    dragging: DragTask | null;
    onCreate: (date: string, title: string) => void;
    onSelect: (task: Task, e: MouseEvent) => void;
    onContextMenu: (task: Task, e: MouseEvent) => void;
    onToggle: (task: Task, checked: boolean) => void;
    onDragStart: (drag: DragTask) => void;
    onDragEnd: () => void;
    // ids is null when the drop left the day's order alone
    onReorder: (input: { ids: number[] | null; task: Task; date: string }) => void;
    onReschedule: (task: Task, date: string) => void;

    // onExpand switches to the view that gives a squeezed day its full room
    onExpand: () => void;
}) {
    const weeks = calendarDays(props.start, props.end);
    const isWeek = props.view !== "month";

    // in the week view each day holding a selected task gets room to read, and the
    // other days squeeze up around them
    const selectedDays = isWeek
        ? props.tasks
            .filter(task => task.id === props.selectedId || !!props.selectedIds?.includes(task.id))
            .map(task => task.showOn)
        : [];

    const columns = weeks[0].map(date => {
        if (collapsesDay(date.day(), props.view)) return "24px";
        if (selectedDays.includes(date.format(dateFormat))) return "minmax(200px, 1fr)";
        return "minmax(0, 1fr)";
    });

    const byDate = new Map<string, Task[]>();
    props.tasks.forEach(task => {
        if (!task.showOn) return;

        const day = byDate.get(task.showOn) || [];
        day.push(task);
        byDate.set(task.showOn, day);
    });

    return (
        <Box sx={{
            flex: 1,
            display: "grid",
            gridTemplateRows: "auto",
            gridAutoRows: `minmax(${rowHeight[props.view]}px, 1fr)`,
            // a 0 floor on each column is what lets the week squeeze up when the side
            // panel opens, rather than running off the edge
            gridTemplateColumns: columns.join(" "),
            // the last column has nothing to its right, and the sheet under the grid
            // draws the rule below the last row
            "& > :nth-of-type(7n)": { borderRight: 0 },
            "& > :nth-last-of-type(-n+7)": { borderBottom: 0 },
        }}>
            {weekdayNames.map((name, day) => (
                // the header cells count as grid children alongside the days, so they
                // share their element type and the nth-child edge rules above
                <Typography key={name} component="div" variant="caption" color="text.secondary"
                            align="center"
                            sx={{
                                py: 0.75,
                                borderRight: 1,
                                borderBottom: 1,
                                borderColor: "grey.300",
                                bgcolor: "grey.200",
                                fontWeight: 600,
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                            }}>
                    {collapsesDay(day, props.view) ? name.substring(0, 1) : name.substring(0, 3)}
                </Typography>
            ))}

            {weeks.map(week => week.map(date => {
                const key = date.format(dateFormat);

                if (collapsesDay(date.day(), props.view)) {
                    return (
                        <CalendarCollapsedDay
                            key={key}
                            date={date}
                            count={(byDate.get(key) || []).length}
                            onClick={props.onExpand}
                            dateKey={key}
                            dragging={props.dragging}
                            onDragEnd={props.onDragEnd}
                            onReschedule={props.onReschedule}
                        />
                    );
                }

                return (
                    <CalendarDay
                        key={key}
                        date={date}
                        dateKey={key}
                        alwaysShowAdd={isWeek}
                        tasks={byDate.get(key) || []}
                        muted={props.view === "month" && date.month() !== props.month}
                        selectedId={props.selectedId}
                        selectedIds={props.selectedIds}
                        saving={props.saving}
                        showTeam={props.showTeam}
                        onCreate={title => props.onCreate(key, title)}
                        onSelect={props.onSelect}
                        onContextMenu={props.onContextMenu}
                        onToggle={props.onToggle}
                        dragging={props.dragging}
                        onDragStart={props.onDragStart}
                        onDragEnd={props.onDragEnd}
                        onReorder={props.onReorder}
                        onReschedule={props.onReschedule}
                    />
                );
            }))}
        </Box>
    );
}
